/**
 * File: LegalChronologies.jsx
 * Path: /src/pages/LegalChronologies.jsx
 * Description: Dedicated page showcasing Veritect.ai's Legal Chronologies AI solution
 * Key Features: Interactive visualization, feature breakdown, and benefits section
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

// Dashboard component to visualize the Legal Chronologies feature
const LegalChronologiesDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-purple-700 to-indigo-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Legal Chronologies AI</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Timeline visualization */}
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-16 top-0 bottom-0 w-0.5 bg-purple-300/50"></div>
                
                {/* Timeline events */}
                {[
                  { date: "2023-04-15", title: "Initial Patient Consultation", content: "Patient reports persistent lower back pain following workplace incident" },
                  { date: "2023-05-23", title: "MRI Diagnostic Imaging", content: "Results show L4-L5 disc herniation with nerve compression" },
                  { date: "2023-06-10", title: "Physical Therapy Begins", content: "PT assessment indicates limited range of motion and significant pain" },
                  { date: "2023-08-18", title: "Orthopedic Specialist Referral", content: "Specialist recommends continued conservative treatment" }
                ].map((event, index) => (
                  <div key={index} className="flex mb-4 relative z-10">
                    <div className="w-16 text-xs text-white/80 pt-0.5">{event.date.split('-')[0]}-{event.date.split('-')[1]}</div>
                    <div className="flex items-center justify-center mr-4">
                      <div className="w-4 h-4 rounded-full bg-purple-400 shadow-glow"></div>
                    </div>
                    <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <div className="text-sm font-medium mb-1">{event.title}</div>
                      <div className="text-xs text-white/80">{event.content}</div>
                    </div>
                  </div>
                ))}
                
                {/* AI-generated elements */}
                <div className="flex mb-4 relative z-10">
                  <div className="w-16 text-xs text-white/80 pt-0.5">AI Analysis</div>
                  <div className="flex items-center justify-center mr-4">
                    <div className="w-4 h-4 rounded-full bg-accent-400 animate-pulse shadow-glow-accent"></div>
                  </div>
                  <div className="flex-1 bg-accent-500/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-sm font-medium mb-1">Key Timeline Insights</div>
                    <div className="text-xs text-white/90">
                      <ul className="space-y-1">
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                          <span>38 days between incident report and diagnostic imaging</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                          <span>Medical records indicate consistent documentation of symptoms</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                          <span>Treatment adherence documented throughout medical history</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress Indicators */}
            <div className="flex space-x-4">
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Analyzed Documents</div>
                <div className="text-xs font-medium">143/143</div>
              </div>
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Events Extracted</div>
                <div className="text-xs font-medium">27</div>
              </div>
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Time Saved</div>
                <div className="text-xs font-medium">8.5 hours</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Legal Chronologies document visualization component
const ChronologyDocumentDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-primary-100 to-purple-100 p-1">
        <div className="bg-white rounded-lg p-6 relative">
          {/* Document Analysis Visualization */}
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="document-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#document-pattern)" />
            </svg>
          </div>
          
          {/* Dashboard container with min-height to prevent cutting off */}
          <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
            <div className="w-full">
              {/* Mockup content */}
              <div className="flex flex-col h-full relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-dark-600">AI-Generated Chronology</span>
                  </div>
                  <div className="flex space-x-3">
                    <div className="py-1 px-3 text-xs bg-purple-50 text-purple-600 rounded-full">Complete</div>
                    <div className="py-1 px-3 text-xs bg-green-50 text-green-600 rounded-full">27 Events</div>
                  </div>
                </div>
                
                {/* Document preview with highlights */}
                <div className="flex-grow grid grid-cols-5 gap-6">
                  <div className="col-span-3 border border-dark-100 rounded-lg p-4 text-sm text-dark-700">
                    <div className="text-lg font-bold mb-3">Case Chronology - Johnson v. Allied Manufacturing</div>
                    <div className="mb-2">RE: Personal Injury - Workplace Accident</div>
                    <div className="mb-4">DATE: May 4, 2025</div>
                    
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 border border-gray-200">Date</th>
                          <th className="p-2 border border-gray-200">Event</th>
                          <th className="p-2 border border-gray-200">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-2 border border-gray-200">2023-04-15</td>
                          <td className="p-2 border border-gray-200">
                            <span className="font-medium">Initial Consultation</span><br />
                            Patient reports persistent lower back pain following workplace incident when lifting heavy equipment.
                          </td>
                          <td className="p-2 border border-gray-200">Medical Record - Dr. Sarah Lee, Primary Care</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-2 border border-gray-200">2023-05-23</td>
                          <td className="p-2 border border-gray-200">
                            <span className="font-medium">MRI Diagnostic Imaging</span><br />
                            <span className="bg-purple-100 rounded px-1">Results show L4-L5 disc herniation with nerve compression.</span> Radiologist notes "significant impingement" of spinal nerve.
                          </td>
                          <td className="p-2 border border-gray-200">Riverside Imaging Center - MRI Report</td>
                        </tr>
                        <tr>
                          <td className="p-2 border border-gray-200">2023-06-10</td>
                          <td className="p-2 border border-gray-200">
                            <span className="font-medium">Physical Therapy Begins</span><br />
                            PT assessment indicates limited range of motion and significant pain during basic movements. Care plan established for 8 weeks.
                          </td>
                          <td className="p-2 border border-gray-200">Metro Physical Therapy - Initial Assessment</td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <div className="mt-4 text-xs text-gray-500">
                      * Chronology includes 27 total events extracted from 143 documents. Full report available for download.
                    </div>
                  </div>
                  
                  <div className="col-span-2 flex flex-col space-y-4">
                    <div className="bg-purple-50 rounded-lg p-4 flex-1">
                      <div className="text-purple-600 font-semibold mb-2 text-sm">Key Insights</div>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5 mr-2 text-xs">1</div>
                          <div>
                            <div className="font-medium">Treatment Gap Identified</div>
                            <div className="text-dark-500 text-xs">38-day delay between initial report and diagnostic imaging</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5 mr-2 text-xs">2</div>
                          <div>
                            <div className="font-medium">Consistent Documentation</div>
                            <div className="text-dark-500 text-xs">Symptoms consistently reported across all medical providers</div>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5 mr-2 text-xs">3</div>
                          <div>
                            <div className="font-medium">Treatment Adherence</div>
                            <div className="text-dark-500 text-xs">Patient attended 93% of scheduled appointments</div>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-primary-50 rounded-lg p-4 flex-1">
                      <div className="text-primary-600 font-semibold mb-2 text-sm">Document Sources</div>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-start justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="font-medium">Medical Records</div>
                          </div>
                          <div className="text-xs text-dark-400">58</div>
                        </li>
                        <li className="flex items-start justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="font-medium">Employment Records</div>
                          </div>
                          <div className="text-xs text-dark-400">24</div>
                        </li>
                        <li className="flex items-start justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="font-medium">Correspondence</div>
                          </div>
                          <div className="text-xs text-dark-400">36</div>
                        </li>
                        <li className="flex items-start justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mt-0.5 mr-2 text-xs">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="font-medium">Other Documents</div>
                          </div>
                          <div className="text-xs text-dark-400">25</div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LegalChronologies = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Features data
  const features = [
    {
      title: "Automated Timeline Creation",
      description: "Automatically extract key dates, events, and facts from thousands of documents to create comprehensive case chronologies.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Smart Document Processing",
      description: "Our AI understands multiple document types including medical records, emails, pleadings, and depositions, accurately extracting relevant facts.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Inconsistency Detection",
      description: "Identify discrepancies and contradictions between documents and witness statements to strengthen your case preparation.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    },
    {
      title: "Customizable Formats",
      description: "Generate chronologies in multiple formats including detailed spreadsheets, narrative summaries, and visual timelines for different needs.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      )
    },
    {
      title: "Source Citation & Verification",
      description: "Every event includes automatic source citations and document links, making verification easy and ensuring defensible chronologies.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      title: "Collaborative Editing",
      description: "Share chronologies with your team for collaborative review and editing with version control and change tracking.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ];

  // How it works steps
  const workflowSteps = [
    {
      number: "01",
      title: "Upload Documents",
      description: "Upload case documents of any type – medical records, emails, depositions, pleadings – in their native format."
    },
    {
      number: "02",
      title: "AI Processing",
      description: "Our system automatically identifies dates, events, and key facts across all documents."
    },
    {
      number: "03",
      title: "Review Timeline",
      description: "Review the generated chronology with every event linked to its source document for easy verification."
    },
    {
      number: "04",
      title: "Export & Share",
      description: "Export in your preferred format (spreadsheet, narrative, timeline) and share with your team or clients."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-purple-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-indigo-100 rounded-tr-full opacity-70"></div>
        
        {/* Floating decorative elements */}
        <motion.div 
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{ 
            y: [0, -15, 0], 
            opacity: [0.4, 0.7, 0.4] 
          }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
        >
          <div className="w-16 h-16 border-2 border-purple-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div 
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{ 
            y: [0, 15, 0], 
            opacity: [0.3, 0.6, 0.3] 
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity, 
            repeatType: "reverse", 
            delay: 1 
          }}
        >
          <div className="w-12 h-12 border-2 border-indigo-500/30 rounded-full"></div>
        </motion.div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  AI-Powered <span className="gradient-text">Legal Chronologies</span> Platform
                </h1>
                <p className="text-xl text-dark-600 mb-8">
                  Create comprehensive case timelines in minutes, not days. Our AI automatically extracts key dates, events, and facts from thousands of documents with unparalleled accuracy.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-10">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/contact"
                    className="shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Request a Demo</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    to="/solutions/legal-chronologies#how-it-works"
                  >
                    See How It Works
                  </Button>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex -space-x-2">
                    {['#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6'].map((color, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, zIndex: 4-i }}>
                        {['P', 'I', 'M', 'L'][i]}
                      </div>
                    ))}
                  </div>
                  <div className="text-dark-500">
                    <strong className="text-dark-700">95%</strong> time reduction
                  </div>
                </div>
              </motion.div>
              
              {/* Right Side - Interactive Visualization */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {/* Legal Chronologies Dashboard Component */}
                <LegalChronologiesDashboard />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <ModernStatsSection 
        title="Transform Your Case Preparation"
        description="See the real impact our AI-powered legal chronologies platform is making for law firms."
        stats={[
          { number: '95%', label: 'Time Saved on Chronologies' },
          { number: '4.8x', label: 'More Documents Analyzed' },
          { number: '89%', label: 'Discovery Efficiency Increase' },
          { number: '73%', label: 'More Factual Insights Uncovered' }
        ]} 
      />
      
      {/* Features Section */}
      <section className="py-20 bg-white" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Key Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Powerful Chronology Capabilities
            </h2>
            <p className="text-dark-500 text-lg">
              Our legal chronologies platform combines cutting-edge AI with deep legal expertise 
              to deliver comprehensive case timelines that are faster, more accurate, and more insightful.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full flex flex-col items-start">
                  <div className="rounded-lg p-3 bg-purple-50 text-purple-600 mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-dark-500">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl"></div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Case Chronologies, Reimagined
            </h2>
            <p className="text-dark-500 text-lg">
              Our intuitive workflow makes creating comprehensive case chronologies faster and more effective than ever before.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                {/* Connector line between steps (hidden on mobile) */}
                {index < workflowSteps.length - 1 && (
                  <div className="absolute hidden lg:block top-6 left-full w-full h-0.5 bg-purple-200 z-0">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45">
                      <svg className="w-3 h-3 text-purple-400" fill="currentColor" viewBox="0 0 256 512">
                        <path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path>
                      </svg>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-purple-600 text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-primary-600 font-semibold">Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Why Choose Veritect Legal Chronologies AI
              </h2>
              <p className="text-dark-600 mb-8">
                Our AI-powered legal chronologies platform doesn't just save time—it transforms how legal professionals
                approach case preparation, enabling deeper insights and better case outcomes.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Dramatic Time Savings",
                    description: "What takes paralegals and associates weeks to complete manually can be done in minutes, freeing up valuable time for strategic case work."
                  },
                  {
                    title: "Comprehensive Coverage",
                    description: "AI processing ensures no important dates or events are missed, analyzing more documents with greater thoroughness than humanly possible."
                  },
                  {
                    title: "Enhanced Case Preparation",
                    description: "Identify critical timeline gaps, inconsistencies, and patterns that strengthen your case strategy and witness preparation."
                  },
                  {
                    title: "Improved Client Outcomes",
                    description: "Better prepared cases lead to more favorable settlements, verdicts, and client satisfaction, enhancing your firm's reputation."
                  }
                ].map((benefit, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="mr-4 text-purple-600 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{benefit.title}</h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10">
                <Button 
                  variant="primary"
                  to="/contact"
                  className="shadow-lg"
                >
                  Schedule a Demo
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Legal Chronology Document Dashboard */}
              <ChronologyDocumentDashboard />
              
              {/* Floating elements for visual interest */}
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-purple-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonial Section */}
      <section className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-400 via-purple-400 to-indigo-400"></div>
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
            </g>
          </svg>
        </div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Hear From Our Clients
            </h2>
            <p className="text-dark-500 text-lg">
              Leading law firms are transforming their case preparation with Veritect.ai
            </p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 relative max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Quote mark */}
            <div className="absolute top-6 left-8 text-purple-300 opacity-30 text-6xl font-serif">
              "
            </div>
            
            <div className="pl-8">
              <blockquote className="text-lg mb-6 text-dark-700">
                The legal chronologies tool has revolutionized our personal injury practice. For a complex 
                medical malpractice case with thousands of records spanning five years, we created a 
                comprehensive timeline in hours, not weeks. The AI identified critical timeline inconsistencies 
                that became pivotal in our cross-examination strategy, ultimately leading to a favorable settlement.
              </blockquote>
              
              <div className="flex items-center">
                <div className="mr-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center text-white text-lg font-bold">
                    ET
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800">Emily Thompson</h4>
                  <p className="text-dark-500 text-sm">Litigation Partner, Thompson Legal</p>
                </div>
                <div className="ml-auto flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Everything you need to know about our Legal Chronologies AI platform
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "What types of documents can your platform process?",
                answer: "Our platform can process virtually any document type including PDFs, Word documents, emails, scanned medical records, deposition transcripts, pleadings, and more. The AI can extract dates and events from both text-searchable documents and scanned images using advanced OCR technology."
              },
              {
                question: "How accurate is the event extraction?",
                answer: "Our platform achieves 93% accuracy in identifying relevant dates and events across document types. Every extracted event includes a source citation linking back to the original document, allowing for easy verification. The system improves over time as it learns from your team's feedback and corrections."
              },
              {
                question: "Can I customize how chronologies are organized and displayed?",
                answer: "Yes, the platform offers extensive customization options. You can create multiple views of the same data (detailed spreadsheets, narrative summaries, or visual timelines), filter events by date ranges or categories, highlight key events, and add custom notes or tags to specific entries."
              },
              {
                question: "How does the system handle inconsistencies between documents?",
                answer: "The AI automatically flags inconsistencies between documents, such as conflicting dates or contradictory descriptions of the same event. These inconsistencies are highlighted in the chronology with links to the source documents, allowing your team to investigate further and develop case strategy around these discrepancies."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Ready to Transform Your Case Preparation?"
        description="Join innovative law firms using Veritect.ai to create comprehensive case chronologies in minutes, not weeks."
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
    </motion.div>
  );
};

export default LegalChronologies;